import React, { useState, useEffect } from "react";
import { PlusIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { createTask } from "../functions/api_fethcers";

/**
 * QuickTaskCreate component
 * Allows creating a new task quickly for a project when starting a timer
 */
const QuickTaskCreate = ({ projects = [], onTaskCreated }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [taskName, setTaskName] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Reset form values when form is closed
  useEffect(() => {
    if (!isFormOpen) {
      setTaskName("");
      setError("");
    }
  }, [isFormOpen]);

  // Select first project by default if available and none selected
  useEffect(() => {
    if (projects.length > 0 && !selectedProject) {
      console.log(
        "No project selected, defaulting to first project:",
        projects[0]
      );
      setSelectedProject(projects[0]._id);
    }
  }, [projects, selectedProject]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!taskName.trim()) {
      setError("Task name is required");
      return;
    }

    if (!selectedProject) {
      setError("Please select a project");
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      const taskData = {
        name: taskName.trim(),
        status: "to_do",
        priority: "medium",
      };

      console.log("Creating task with data:", taskData);
      console.log("Selected project ID:", selectedProject);

      const newTask = await createTask({
        projectId: selectedProject,
        taskData,
      });

      // Add project info to task for display purposes
      const projectInfo = projects.find((p) => p.id === selectedProject);
      newTask.project_name = projectInfo?.name || "Unknown Project";

      setIsFormOpen(false);
      setTaskName("");

      // Notify parent component with the new task
      if (onTaskCreated) {
        onTaskCreated(newTask);
      }
    } catch (error) {
      console.error("Failed to create task:", error);
      setError(error.message || "Failed to create task");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isFormOpen) {
    return (
      <button
        onClick={() => setIsFormOpen(true)}
        className="w-full flex items-center justify-center gap-1 px-3 py-2 mt-2 text-sm 
                  bg-grayteck-50 dark:bg-darkbg-700 hover:bg-grayteck-100 dark:hover:bg-darkbg-600 
                  text-grayteck-600 dark:text-grayteck-300 rounded-md transition-colors
                  border border-dashed border-grayteck-300 dark:border-darkbg-600"
      >
        <PlusIcon className="w-4 h-4 text-grayteck-500 dark:text-grayteck-400" />
        <span>Create New Task</span>
      </button>
    );
  }

  return (
    <div className="mt-2 p-3 border border-grayteck-200 dark:border-darkbg-700 rounded-md bg-white dark:bg-darkbg-800">
      <form onSubmit={handleSubmit}>
        <div className="mb-2">
          <label
            htmlFor="task-name"
            className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
          >
            Task Name
          </label>
          <input
            type="text"
            id="task-name"
            value={taskName}
            onChange={(e) => setTaskName(e.target.value)}
            placeholder="Enter task name"
            className="w-full px-3 py-2 border border-grayteck-300 dark:border-darkbg-600 rounded-md 
                      bg-white dark:bg-darkbg-800 text-grayteck-800 dark:text-grayteck-200
                      focus:outline-none focus:ring-2 focus:ring-grayteck-500"
            autoFocus
          />
        </div>

        <div className="mb-3">
          <label
            htmlFor="project"
            className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
          >
            Project
          </label>
          <select
            id="project"
            value={selectedProject}
            onChange={(e) => {
              console.log("Project selected:", e.target.value);
              setSelectedProject(e.target.value);
            }}
            className="w-full px-3 py-2 border border-grayteck-300 dark:border-darkbg-600 rounded-md 
                      bg-white dark:bg-darkbg-800 text-grayteck-800 dark:text-grayteck-200
                      focus:outline-none focus:ring-2 focus:ring-grayteck-500"
          >
            {projects.length === 0 ? (
              <option value="" disabled>
                No projects available
              </option>
            ) : (
              projects.map((project) => (
                <option key={project._id} value={project._id}>
                  {project.name}
                </option>
              ))
            )}
          </select>
        </div>

        {error && (
          <div className="mb-3 text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setIsFormOpen(false)}
            className="px-3 py-1.5 text-sm bg-grayteck-200 hover:bg-grayteck-300 
                      dark:bg-darkbg-700 dark:hover:bg-darkbg-600
                      text-grayteck-800 dark:text-grayteck-200 rounded-md"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
          <button
            type="submit"
            disabled={isLoading || !selectedProject}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-500 hover:bg-green-600 
                    text-white rounded-md disabled:opacity-50"
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <CheckIcon className="w-4 h-4" />
            )}
            <span>Create Task</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuickTaskCreate;
